import { type CSSProperties, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * An info icon (ⓘ) that opens a floating explanation bubble on tap.
 * The bubble stays visible until the user taps outside or scrolls.
 *
 * Rendered as a <span> so it's safe to nest inside list-item link anchors
 * (no <a>-inside-<a> violation). The `slot` prop is forwarded so F7's slot
 * bucketing works when placed inside ListItem.
 */
export function InfoTip({
  text,
  slot,
  style,
  'aria-label': ariaLabel,
}: {
  text: string;
  slot?: string;
  style?: CSSProperties;
  'aria-label'?: string;
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number; above: boolean }>({
    top: 0,
    left: 0,
    above: false,
  });
  const iconRef = useRef<HTMLSpanElement>(null);

  const toggle = (e: React.MouseEvent) => {
    // Stop propagation so the document close-listener (added when open=true)
    // doesn't immediately dismiss the tooltip we're about to show, and so
    // clicking the icon doesn't also fire the parent row's action.
    e.stopPropagation();

    if (open) {
      setOpen(false);
      return;
    }

    const rect = iconRef.current?.getBoundingClientRect();
    if (!rect) return;

    const above = rect.bottom + 8 + 120 > window.innerHeight;
    setPos({
      top: above ? rect.top - 8 : rect.bottom + 8,
      left: Math.min(Math.max(rect.left + rect.width / 2, 16), window.innerWidth - 16),
      above,
    });
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    document.addEventListener('click', close);
    document.addEventListener('scroll', close, { capture: true });
    return () => {
      document.removeEventListener('click', close);
      document.removeEventListener('scroll', close, true);
    };
  }, [open]);

  return (
    <span
      ref={iconRef}
      slot={slot}
      aria-label={ariaLabel}
      onClick={toggle}
      style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer', ...style }}
    >
      <i className="f7-icons" style={{ fontSize: 18, color: 'var(--f7-theme-color)' }}>
        info_circle
      </i>
      {open &&
        createPortal(
          <div
            className={`infotip-bubble${pos.above ? ' infotip-bubble--above' : ''}`}
            style={{ top: pos.top, left: pos.left }}
            // Prevent the close-listener from firing when tapping inside the bubble.
            onClick={(e) => e.stopPropagation()}
          >
            {text}
          </div>,
          document.body,
        )}
    </span>
  );
}
